import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Star, StarOff, Eye, EyeOff, Trash2, Zap, ZapOff, Crown, Sparkles, BrainCircuit, Upload, Image as ImageIcon } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { Article } from "@/lib/types";
import { PageHeader, Table, StatusBadge } from "@/admin/ui";
import { ArticleIntelligenceDialog } from "@/admin/ArticleIntelligence";
import { LiveUpdatesPanel } from "@/admin/LiveUpdatesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function htmlToPlain(htmlStr: string): string | null {
  if (!htmlStr || !htmlStr.trim()) return "";
  const div = document.createElement("div");
  div.innerHTML = htmlStr;
  const out: string[] = [];
  for (const n of Array.from(div.childNodes)) {
    if (n.nodeType === Node.TEXT_NODE) {
      if ((n.textContent || "").trim()) return null;
      continue;
    }
    const el = n as HTMLElement;
    if (el.tagName !== "P" || el.querySelector("*:not(br)")) return null;
    out.push((el.textContent || "").trim());
  }
  return out.filter(Boolean).join("\n\n");
}

function plainToHtml(text: string): string {
  const esc = (s: string) => {
    const d = document.createElement("div");
    d.textContent = s;
    return d.innerHTML;
  };
  return text.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br/>")}</p>`).join("");
}

export default function Articles() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Article | null>(null);
  const [intel, setIntel] = useState<Article | null>(null);
  const [form, setForm] = useState<any>({});
  const [genBusy, setGenBusy] = useState(false);
  const [dupFindings, setDupFindings] = useState<any[] | null>(null);
  const [dupOverride, setDupOverride] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bodyMode, setBodyMode] = useState<"plain" | "html">("plain");
  const [bodyTouched, setBodyTouched] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) { setDupFindings(null); setDupOverride(false); return; }
    const url = (form.hero_image_url || "").trim();
    if (!url || url === (editing.hero_image_url || "")) {
      setDupFindings(null); setDupOverride(false); return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await api.post("/admin/media/check-duplicate", { url, article_id: editing.id });
        setDupFindings(r.data.duplicate ? r.data.findings : null);
        setDupOverride(false);
      } catch { setDupFindings(null); }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.hero_image_url, editing]);

  const generateDeepDive = async () => {
    setGenBusy(true);
    try {
      const res = await api.post("/admin/deep-dive/generate");
      toast.success(res.data.message);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenBusy(false); }
  };

  const generateWeeklyRecap = async () => {
    setGenBusy(true);
    try {
      const res = await api.post("/admin/weekly-recap/generate");
      toast.success(res.data.message);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenBusy(false); }
  };

  const { data } = useQuery({
    queryKey: ["admin-articles", status, search, page],
    queryFn: async () => (await api.get("/admin/articles", { params: { status, search: search || undefined, page } })).data,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-articles"] });
    queryClient.invalidateQueries({ queryKey: ["homepage"] });
  };

  const patch = async (id: string, payload: any, msg: string) => {
    try {
      await api.patch(`/admin/articles/${id}`, payload);
      toast.success(msg);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (a: Article) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Article deleted");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (a: Article) => {
    const raw = (a as any).body_sanitized || a.body_html || "";
    const plain = htmlToPlain(raw);
    setBodyMode(plain === null ? "html" : "plain");
    setBodyTouched(false);
    setEditing(a);
    setForm({ title: a.title, summary: a.summary_ai || a.summary, hero_image_url: a.hero_image_url,
      categories: (a.categories || []).join(", "), tags: (a.tags || []).join(", "), access_level: a.access_level,
      live_state: a.live_state || "standard", author_name: a.author_name || "",
      body: plain === null ? raw : plain });
  };

  const uploadHero = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/articles/upload-image", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f: any) => ({ ...f, hero_image_url: r.data.image_url }));
      toast.success("Image uploaded — hit Save changes to apply it");
    } catch (e) { toast.error(apiError(e)); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (dupFindings && !dupOverride) {
      toast.error("Duplicate media detected — confirm 'Use anyway' under the image field to assign it intentionally.");
      return;
    }
    try {
      const payload: any = {
        title: form.title, summary: form.summary, hero_image_url: form.hero_image_url,
        categories: form.categories.split(",").map((s: string) => s.trim()).filter(Boolean),
        tags: form.tags.split(",").map((s: string) => s.trim()).filter(Boolean),
        access_level: form.access_level,
        live_state: form.live_state,
      };
      if ((form.author_name || "").trim()) payload.author_name = form.author_name.trim();
      if (bodyTouched && (form.body || "").trim()) {
        payload.body_html = bodyMode === "plain" ? plainToHtml(form.body) : form.body;
      }
      await api.patch(`/admin/articles/${editing.id}`, payload);
      toast.success("Article updated");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-articles-page">
      <PageHeader title="Articles" sub="Override, feature, hide or publish any imported or editorial story"
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button onClick={generateWeeklyRecap} disabled={genBusy} data-testid="generate-weekly-recap-button"
              className="bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/20">
              <Sparkles className="h-4 w-4 mr-1.5" /> {genBusy ? "Starting…" : "Generate Weekly Recap"}
            </Button>
            <Button onClick={generateDeepDive} disabled={genBusy} data-testid="generate-deep-dive-button"
              className="bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/40 hover:bg-[#F59E0B]/25">
              <Sparkles className="h-4 w-4 mr-1.5" /> {genBusy ? "Starting…" : "Generate Premium Deep Dive"}
            </Button>
            <Button data-testid="generate-radar-button"
              onClick={async () => { try { const r = await api.post("/admin/radar/generate"); toast.success(r.data.message); } catch (e) { toast.error(apiError(e)); } }}
              className="bg-[#7C3AED]/15 text-[#A78BFA] border border-[#7C3AED]/40 hover:bg-[#7C3AED]/25">
              <Sparkles className="h-4 w-4 mr-1.5" /> Send Regulation Radar
            </Button>
            <Button data-testid="backfill-real-images-button"
              onClick={async () => {
                try {
                  const r = await api.post("/admin/articles/backfill-real-images");
                  const d = r.data;
                  toast.success(d.processed != null
                    ? `${d.message} — ${d.processed}/${d.targets} done, ${d.replaced} real photos recovered`
                    : d.message);
                } catch (e) { toast.error(apiError(e)); }
              }}
              className="bg-[#22C55E]/10 text-[#4ADE80] border border-[#22C55E]/40 hover:bg-[#22C55E]/20">
              <ImageIcon className="h-4 w-4 mr-1.5" /> Recover Real Images
            </Button>
            <Button data-testid="generate-brief-audio-button"
              onClick={async () => { try { const r = await api.post("/admin/brief-audio/generate"); toast.success(r.data.message); } catch (e) { toast.error(apiError(e)); } }}
              className="bg-white/5 text-gray-200 border border-white/20 hover:bg-white/10">
              <Sparkles className="h-4 w-4 mr-1.5" /> Generate Brief Audio
            </Button>
          </div>
        } />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search titles..." data-testid="articles-search-input"
          className="bg-[#1A1A1A] border-white/10 text-white max-w-xs" />
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white w-[200px]" data-testid="articles-status-filter">
            <SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 text-white">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="imported_pending_review">Pending review</SelectItem>
            <SelectItem value="hidden">Hidden</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Table headers={["Title", "Source", "Status", "Conf.", "Views", "When", "Actions"]}>
        {(data?.items || []).map((a: Article) => (
          <tr key={a.id} data-testid={`article-row-${a.id}`}>
            <td className="px-4 py-2.5 max-w-[340px]">
              <p className="text-white truncate">{a.is_featured && <Star className="inline h-3 w-3 text-[#FFD600] mr-1" />}{a.title}</p>
              <p className="font-mono text-[10px] text-gray-600">{(a.categories || []).join(", ")}</p>
            </td>
            <td className="px-4 py-2.5 text-gray-400 text-xs">{a.source_name}</td>
            <td className="px-4 py-2.5"><StatusBadge status={a.status || ""} /></td>
            <td className="px-4 py-2.5 font-mono text-xs text-gray-400">
              {a.import_confidence_score != null ? `${Math.round(a.import_confidence_score * 100)}%` : "—"}
            </td>
            <td className="px-4 py-2.5 font-mono text-xs text-gray-400">{a.views}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{timeAgo(a.published_at)}</td>
            <td className="px-4 py-2.5">
              <div className="flex gap-0.5">
                <Button size="icon" variant="ghost" onClick={() => openEdit(a)} title="Edit"
                  data-testid={`article-edit-${a.id}`} className="text-gray-400 hover:text-white h-8 w-8">
                  <Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setIntel(a)} title="Intelligence overrides"
                  data-testid={`article-intelligence-${a.id}`} className="text-[#00E5FF]/70 hover:text-[#00E5FF] h-8 w-8">
                  <BrainCircuit className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" title={a.is_featured ? "Unfeature" : "Feature"}
                  data-testid={`article-feature-${a.id}`}
                  onClick={() => patch(a.id, { is_featured: !a.is_featured }, a.is_featured ? "Unfeatured" : "Featured on homepage")}
                  className="text-[#FFD600] hover:text-yellow-300 h-8 w-8">
                  {a.is_featured ? <StarOff className="h-3.5 w-3.5" /> : <Star className="h-3.5 w-3.5" />}</Button>
                <Button size="icon" variant="ghost" title={a.is_breaking ? "Remove breaking" : "Mark breaking"}
                  data-testid={`article-breaking-${a.id}`}
                  onClick={() => patch(a.id, { is_breaking: !a.is_breaking } as any,
                    a.is_breaking ? "Breaking flag removed" : "Marked as BREAKING")}
                  className="text-red-400 hover:text-red-300 h-8 w-8">
                  {a.is_breaking ? <ZapOff className="h-3.5 w-3.5" /> : <Zap className="h-3.5 w-3.5" />}</Button>
                <Button size="icon" variant="ghost" title={a.access_level === "premium" ? "Make free" : "Mark premium"}
                  data-testid={`article-premium-${a.id}`}
                  onClick={() => patch(a.id, { access_level: a.access_level === "premium" ? "free" : "premium" },
                    a.access_level === "premium" ? "Now free to read" : "Marked PREMIUM — paywalled")}
                  className={`h-8 w-8 ${a.access_level === "premium" ? "text-[#F59E0B]" : "text-gray-500"} hover:text-[#F59E0B]`}>
                  <Crown className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" title={a.status === "published" ? "Hide" : "Publish"}
                  data-testid={`article-toggle-status-${a.id}`}
                  onClick={() => patch(a.id, { status: a.status === "published" ? "hidden" : "published" },
                    a.status === "published" ? "Hidden from site" : "Published")}
                  className="text-gray-400 hover:text-white h-8 w-8">
                  {a.status === "published" ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</Button>
                <Button size="icon" variant="ghost" onClick={() => remove(a)} title="Delete"
                  data-testid={`article-delete-${a.id}`} className="text-gray-500 hover:text-red-400 h-8 w-8">
                  <Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </td>
          </tr>
        ))}
      </Table>

      {data && data.pages > 1 && (
        <div className="flex gap-2 mt-4 justify-center">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="border-white/20 bg-transparent text-white">Prev</Button>
          <span className="text-gray-500 text-sm py-1.5 font-mono">{page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)}
            className="border-white/20 bg-transparent text-white">Next</Button>
        </div>
      )}

      <ArticleIntelligenceDialog article={intel} onClose={() => setIntel(null)} onSaved={refresh} />

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Edit article</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label>
              <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="article-form-title" className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div><Label>Summary</Label>
              <Textarea value={form.summary || ""} onChange={(e) => setForm({ ...form, summary: e.target.value })}
                data-testid="article-form-summary" className="mt-1 bg-[#1A1A1A] border-white/10 min-h-[100px]" /></div>
            <div><Label>Author byline</Label>
              <Input value={form.author_name || ""} onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                placeholder="Displayed author name" data-testid="article-form-author"
                className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div><Label>Hero image</Label>
              {form.hero_image_url ? (
                <img src={form.hero_image_url} alt="Hero preview" data-testid="article-form-image-preview"
                  className="mt-2 w-full max-h-48 object-cover rounded-lg border border-white/10" />
              ) : (
                <p className="mt-2 text-xs text-gray-500" data-testid="article-form-no-image">No image assigned</p>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="article-form-image-file"
                onChange={(e) => e.target.files?.[0] && uploadHero(e.target.files[0])} />
              <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="article-form-image-upload"
                className="mt-2 h-8 bg-[#7C3AED]/15 text-[#A78BFA] border border-[#7C3AED]/40 hover:bg-[#7C3AED]/25">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Upload new image"}
              </Button>
              <Input value={form.hero_image_url || ""} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                placeholder="…or paste an image URL"
                data-testid="article-form-image" className="mt-2 bg-[#1A1A1A] border-white/10" />
              {dupFindings && (
                <div data-testid="media-duplicate-warning"
                  className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="text-amber-300 text-xs font-semibold">Possible duplicate media</p>
                  <ul className="mt-1 space-y-0.5">
                    {dupFindings.map((f: any, i: number) => (
                      <li key={i} className="text-amber-200/80 text-xs">
                        {f.kind === "appears_in_body"
                          ? "This image already appears inside the article body"
                          : `Already used as hero on “${f.title}”`} · {String(f.method || "").replace(/_/g, " ")}
                      </li>
                    ))}
                  </ul>
                  {!dupOverride ? (
                    <Button size="sm" data-testid="media-duplicate-override" onClick={() => setDupOverride(true)}
                      className="mt-2 h-7 bg-amber-500/20 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30">
                      Use anyway — intentional repeat
                    </Button>
                  ) : (
                    <p className="mt-2 text-[11px] text-amber-300/80" data-testid="media-duplicate-overridden">
                      Override confirmed — this image will be assigned intentionally.
                    </p>
                  )}
                </div>
              )}</div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Body{bodyMode === "html" ? " (HTML)" : ""}</Label>
                <span className="text-[10px] font-mono text-gray-500">
                  {bodyMode === "html"
                    ? "Rich formatting — edits apply to the HTML directly"
                    : "Plain text — separate paragraphs with a blank line"}
                </span>
              </div>
              <Textarea value={form.body || ""}
                onChange={(e) => { setForm({ ...form, body: e.target.value }); setBodyTouched(true); }}
                data-testid="article-form-body"
                className="mt-1 bg-[#1A1A1A] border-white/10 min-h-[220px] text-sm" /></div>
            <div><Label>Categories (comma separated slugs)</Label>
              <Input value={form.categories || ""} onChange={(e) => setForm({ ...form, categories: e.target.value })}
                data-testid="article-form-categories" className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div><Label>Tags (comma separated)</Label>
              <Input value={form.tags || ""} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                data-testid="article-form-tags" className="mt-1 bg-[#1A1A1A] border-white/10" /></div>
            <div><Label>Access level</Label>
              <Select value={form.access_level || "free"} onValueChange={(v) => setForm({ ...form, access_level: v })}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label>Live state</Label>
              <Select value={form.live_state || "standard"} onValueChange={(v) => setForm({ ...form, live_state: v })}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10" data-testid="article-form-live-state"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="breaking">Breaking</SelectItem>
                  <SelectItem value="developing">Developing</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select></div>
            {editing && (["breaking", "developing", "live"].includes(form.live_state) ||
              (editing.live_updates || []).length > 0) && (
              <LiveUpdatesPanel articleId={editing.id} initial={(editing.live_updates as any) || []}
                onChanged={refresh} />
            )}
            <Button onClick={saveEdit} data-testid="article-form-save"
              className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
